import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Lock, Mail, ShieldCheck } from 'lucide-react';
import { useAuth, ApiError } from './AuthContext';

const AdminLogin: React.FC = () => {
  const { admin, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && admin) {
    const from = (location.state as { from?: string })?.from || '/proadmin/users';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/proadmin/users', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 px-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-accent-amber flex items-center justify-center">
            <ShieldCheck size={20} className="text-bg-dark" />
          </div>
          <span className="font-figtree font-black text-white text-lg tracking-tight">Azura Admin</span>
        </div>

        <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8 shadow-2xl">
          <h1 className="font-figtree font-bold text-xl text-white mb-1">Sign in</h1>
          <p className="text-neutral-400 text-sm font-exo mb-6">Restricted access. Authorized administrators only.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Email</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  type="email" required autoFocus value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-neutral-950 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-accent-amber transition-all"
                  placeholder="you@azuramobility.com" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  type="password" required value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-neutral-950 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-accent-amber transition-all"
                  placeholder="••••••••" />
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-xs font-exo bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">{error}</p>
            )}

            <button type="submit" disabled={submitting}
              className="w-full py-3 rounded-xl bg-accent-amber text-bg-dark font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white disabled:opacity-60 transition-all">
              {submitting ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>

        <p className="text-center text-neutral-600 text-xs font-exo mt-6">Azura Mobility internal tool</p>
      </div>
    </div>
  );
};

export default AdminLogin;
